import { IconArrow } from "app/components/Icon/Icon";
import ModalMarketTypeGroupings from "app/components/UnsummarizedMarketsModal/components/ModalMarketTypeGroupings/ModalMarketTypeGroupings";
import { UserType } from "app/types/userType";
import {
  EventStatus,
  geniusScoreboardSports,
  PollingIntervals,
  SportsNameType,
  widgetProviders,
} from "app/utils/const";
import isEmpty from "lodash/isEmpty";
import { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { fetchEventDetails } from "../../../../api/eventsApi";
import BetDrawerButton from "../../../../components/BetDrawerButton/BetDrawerButton";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import ScrollToTop from "../../../../components/ScrollToTop/ScrollToTop";
import MarketPeriods from "../../../../components/UnsummarizedMarketsModal/components/ModalMarkets/components/MarketPeriods/MarketPeriods";
import { EventHelper } from "../../../../helpers/eventHelper";
import type { EventType } from "../../../../types/eventType";
import type { RouterMatchType } from "../../../../types/types";
import EventInfoWidget from "./components/EventDetailScreenHeader/components/EventInfoWidget";
import EventDetailScreenHeader from "./components/EventDetailScreenHeader/EventDetailScreenHeader";
import EventStats from "./components/EventStats/EventStats";
import "./EventDetailScreen.scss";

type Props = {
  match: RouterMatchType;
  history: any;
  event: EventType;
  isWebsocketReady: boolean;
  setEvent: (...args: Array<any>) => any;
  updateEvent: (...args: Array<any>) => any;
  loadEvent: (...args: Array<any>) => any;
  resetEvent: (...args: Array<any>) => any;
  subscribeEvent: (...args: Array<any>) => any;
  unsubscribeEvent: (...args: Array<any>) => any;
  isIdle: boolean;
  betSlipList: any;
  marketGroupType: string;
  widgetExpanded: boolean;
  user?: UserType;
  toggleWidget: (widgetExpanded: boolean) => void;
  darkMode: boolean;
};

type State = {
  hasSubscribed: boolean;
  intervalId: any;
  intervalStarted: boolean;
};

// Used in mobile
class EventDetailScreen extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasSubscribed: false,
      intervalId: 0,
      intervalStarted: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.marketGroupType !== this.props.marketGroupType) {
      this.updateEvent();
    }

    if (
      prevProps.user?.preferences?.oddsType !==
      this.props.user?.preferences?.oddsType
    ) {
      this.updateEvent();
    }
  }
  getEventId() {
    const eventId = this.props.match.params.id;
    // The url has eventId-eventName, eg 967994-dallas-mavericks-at-indiana-pacers/
    // extract the id from the string
    const id = eventId.split("-")[0];
    return id;
  }

  componentDidMount() {
    this.props.resetEvent();
    this.updateEvent();
    // Subscribe websockets for event
    this.handleSubscribeEvent();
    this.startIntervalFetchEvent();
    this.props.toggleWidget(false); // To ensure widget is collapsed when opening event details
  }

  componentWillUnmount() {
    // Unsubscribe websockets for event when leaving
    const eventId = this.getEventId();
    const { betSlipList, event } = this.props;
    //
    betSlipList.forEach(betSlip => {
      if (betSlip.id !== event.id) {
        this.props.unsubscribeEvent(eventId);
      }
    });
    //
    this.endIntervalFetchEvent();
  }

  checkIfEventIsInBetslip(eventId) {
    const { betSlipList } = this.props;
    let isEventInBetslip = false;
    betSlipList.length > 0 &&
      betSlipList.forEach(betSlip => {
        if (betSlip.id === eventId) {
          isEventInBetslip = true;
        }
      });
    return isEventInBetslip;
  }

  handleSubscribeEvent = () => {
    const eventId = this.getEventId();

    if (!this.state.hasSubscribed) {
      if (this.props.isWebsocketReady) {
        this.props.subscribeEvent(eventId);
        this.setState({
          hasSubscribed: true,
        });
      } else {
        setTimeout(() => {
          this.handleSubscribeEvent();
        }, 100);
      }
    }
  };

  /**
   * update event every 10 sec
   */
  startIntervalFetchEvent = () => {
    const eventId = this.getEventId();

    if (!this.state.intervalStarted) {
      const intervalId = setInterval(() => {
        // is user session idle stop polling
        if (this.props.isIdle) return;
        fetchEventDetails(eventId, false, this.props.marketGroupType)
          .then(eventData => {
            this.props.updateEvent(eventData);
          })
          .catch(error => {
            throw error;
          });
      }, PollingIntervals.MOBILE_EVENT_DETAILS);
      this.setState({
        intervalId,
        intervalStarted: true,
      });
    }
  };
  endIntervalFetchEvent = () => {
    clearInterval(this.state.intervalId);
    this.setState({
      intervalStarted: false,
    });
  };

  updateEvent = () => {
    const eventId = this.getEventId();
    this.props.loadEvent(eventId, false, this.props.marketGroupType);
  };
  toggleWidgets = () => {
    this.props.toggleWidget(!this.props.widgetExpanded);
  };

  render() {
    const { history, event, widgetExpanded } = this.props;
    const dateTime = event["startTime"];
    const eventIncidents = event["eventIncidents"];
    let eventStatus = event.status;
    if (eventStatus !== undefined) eventStatus = eventStatus.toLowerCase();

    // Show loading spinner if event is not fetched yet
    if (isEmpty(event)) {
      return <LoadingSpinner />;
    }

    let score: string | undefined = undefined;

    if (EventHelper.isEventLive(event) && event["eventIncidents"] != null) {
      // check if extra time is so swap score to extra time score
      const isEventInExtraTime = EventHelper.isEventInExtraTime(event);
      isEventInExtraTime
        ? (score = EventHelper.getScores(
            event["eventIncidents"][0]["currentPeriodScoreDetails"],
            event.showHomePlayerAsFirst
          ))
        : (score = EventHelper.getScores(
            event["eventIncidents"][0]["scoreDetails"],
            event.showHomePlayerAsFirst
          ));

      if (score == null) {
        score = undefined;
      }
    }
    // If widgetProvider is available, then can show a widget

    const provider = event?.widgets?.widgetProvider;
    const isProviderGenius =
      provider === widgetProviders.GENIUSSPORTS ? true : false;
    const isWidgetAvailable = event?.widgets?.widgetProvider ? true : false;
    const isWidgetExpanable = !(
      event.sportType === SportsNameType.CRICKET ||
      geniusScoreboardSports.includes(event?.sportType)
    );
    return (
      <div className={`event-detail`}>
        <EventDetailScreenHeader
          teams={event.translatedName}
          competition={event.competition.translatedName}
          navigationNodeIds={event.competition.navigationNodeIds}
          history={history}
          isRunning={EventHelper.isEventLive(event)}
          score={score}
          allowLiveBetting={EventHelper.isEventGoingLive(event)}
          dateTime={dateTime}
          eventIncidents={eventIncidents}
          event={event}
          fetchEventData={fetchEventDetails}
        />
        {eventStatus === EventStatus.SUSPENDED.toLowerCase() ? (
          <div className="suspended__text--event">Suspended</div>
        ) : (
          ""
        )}
        {/* add show-widget__event-detail-screen style for BR widget layout */}
        <div
          className={`event-detail__content ${eventStatus} ${
            isWidgetAvailable
              ? "event-detail__content__show-widget"
              : "event-detail__content__no-widget"
          } ${widgetExpanded ? "widget-expanded" : "widget-collapsed"}
          ${isProviderGenius && !widgetExpanded && "widget-collapsed-genius"}
           ${
             event.sportType === SportsNameType.CRICKET ? "widget__cricket" : ""
           }`}
        >
          {!isWidgetAvailable &&
            EventHelper.isEventLive(event) &&
            (EventHelper.isEventPhaseOfTypeLive(event) ? (
              <div className="event-detail__content__status">
                <FormattedMessage tagName="span" id="events-dashboard-live" />
              </div>
            ) : (
              <EventStats
                eventStartTime={dateTime}
                eventIncidents={eventIncidents}
                event={event}
              />
            ))}
          {isWidgetAvailable && (
            <EventInfoWidget
              widgets={event?.widgets}
              darkMode={this.props.darkMode}
              sportType={event.sportType}
              showHomePlayerAsFirst={event.showHomePlayerAsFirst}
              event={event}
              widgetExpanded={widgetExpanded}
            />
          )}

          {isWidgetAvailable && isWidgetExpanable && (
            <div
              className={`expand-button ${
                widgetExpanded ? "arrow-up" : "arrow-down"
              }`}
              onClick={() => this.toggleWidgets()}
            >
              <IconArrow />
            </div>
          )}
          <ModalMarketTypeGroupings event={event} sportId={event["sportId"]} />
          {event &&
            event.marketPeriods !== undefined &&
            event.marketPeriods !== null &&
            event.marketPeriods.map((marketPeriod, index) => {
              // if market period returned with no prices
              // display the following no odds available message
              if (event["numberOfMarkets"] === 0) {
                return (
                  <div className="asian-event__no-odds">
                    <FormattedMessage
                      tagName="span"
                      id={"events-no-odds"}
                      className="no-odds-available"
                    />
                  </div>
                );
              }

              return (
                <MarketPeriods
                  key={index}
                  event={this.props.event}
                  marketPeriod={marketPeriod}
                  className="market-period-summary"
                />
              );
            })}
          {event.marketPeriods !== null &&
            event["numberOfMarkets"] === 0 &&
            isEmpty(event.marketPeriods) && (
              <>
                <div className="asian-event__no-odds">
                  <FormattedMessage
                    tagName="span"
                    id={"events-no-odds"}
                    className="no-odds-available"
                  />
                </div>
              </>
            )}
        </div>

        <ScrollToTop />
        <BetDrawerButton />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  event: state.singleEventModel.singleEvent,
  isWebsocketReady: state.subscribeModel.isWebsocketReady,
  isIdle: state.sessionModel.isIdle,
  betSlipList: state.betSlipListModel.betSlipList,
  marketGroupType: state.competitionListModel.marketGroupType,
  widgetExpanded: state.singleEventModel.widgetExpanded,
  user: state.userModel.user,
});

const mapDispatchToProps = dispatch => ({
  setEvent: (event: EventType) => dispatch.singleEventModel.setEvent(event),
  updateEvent: (event: EventType) =>
    dispatch.singleEventModel.updateEvent(event),
  loadEvent: (eventId, updatingEventList, marketGroupType) =>
    dispatch.singleEventModel.loadEvent({
      eventId,
      updatingEventList,
      marketGroupType,
    }),
  resetEvent: () => dispatch.singleEventModel.resetEvent(),
  subscribeEvent: (eventId: number) =>
    dispatch.subscribeModel.subscribeEvent({
      eventId,
    }),
  toggleWidget: (widgetExpanded: boolean) => {
    dispatch.singleEventModel.toggleWidget(widgetExpanded);
  },
  unsubscribeEvent: (eventId: number) =>
    dispatch.subscribeModel.unsubscribeEvent({
      eventId,
    }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventDetailScreen) as any;
